import I18n from 'I18n';
import { Range, List } from 'immutable';
import get from 'transmute/get';
import identity from 'transmute/identity';
import * as TimeRangeListUtils from 'MeetingsBase/utils/TimeRangeListUtils';
import { CUSTOM_AVAILABILITY } from 'MeetingsBase/constants/dates';
import TimeRange from 'MeetingsBase/models/TimeRange';
import enviro from 'enviro';
import formatName from 'I18n/utils/formatName';
export function makeDateTime(_ref) {
  var time = _ref.time,
      date = _ref.date,
      timezone = _ref.timezone;
  return I18n.moment.tz(I18n.moment(date).add(time).format('YYYY-MM-DD HH:mm:ss'), timezone).valueOf();
}
export function timeRangeToDateTimeRange(_ref2) {
  var timeRange = _ref2.timeRange,
      date = _ref2.date,
      timezone = _ref2.timezone;
  return TimeRange.from({
    start: makeDateTime({
      time: timeRange.start,
      date: date,
      timezone: timezone
    }),
    end: makeDateTime({
      time: timeRange.end,
      date: date,
      timezone: timezone
    })
  });
}
export function formatRoundRobinAvailability(availableTimes, allBusyTimes) {
  /**
   * Splits the availability in round robin links when all users in the link
   * have a conflict for a given bookable time slot
   * @param {List} availableTimes - A list of unified TimeRanges that can be booked
   * @param {List} allBusyTimes - A list of the busy times for each
   * user on the link. List([List([TimeRange, ...]), ...])
   * @return {List} A TimeRanges split by user availability
   */
  return availableTimes.reduce(function (acc, value) {
    var busyTimeIntersections = allBusyTimes.map(function (userBusyTimes) {
      return TimeRangeListUtils.timeRangesOfIntersection(value, userBusyTimes);
    });

    if (busyTimeIntersections.every(function (busyTimes) {
      return busyTimes.size > 0;
    })) {
      // If all users have a conflict in this bookable time slot, split up the
      // time slot using the availability of each user separately.
      return acc.concat(busyTimeIntersections.map(function (busyTimes) {
        return TimeRangeListUtils.negate(busyTimes, value.get('start'), value.get('end'));
      }).flatten(true));
    }

    return acc.push(value);
  }, List());
}
export function getListOfDays(startTime, daysToAdvertise) {
  /**
   * @param {moment} startTime - Timestamp to start counting days from
   * @param {int} daysToAdvertise - Number of days to list
   * @return {List} List of moments, at the start of each day, at the same
   * time each day, exactly one day apart
   */
  return Range().take(daysToAdvertise).toList().map(function (numDays) {
    return startTime.clone().add(numDays, 'days');
  });
}
export function getBookableDays(weeksToAdvertise, customAvailabilityStartDate, customAvailabilityEndDate, now) {
  /**
   * @param {int} weeksToAdvertise - Number of weeks ahead a meeting can be booked
   * @param {int} customAvailabilityStartDate - Date custom availability starts
   * @param {int} customAvailabilityEndDate - Date custom availability ends
   * @param {int} now - Timestamp representing this moment
   * @return {List} List of moments, at the start of each available day
   * in UTC timezone
   */
  var hasCustomAvailability = weeksToAdvertise === CUSTOM_AVAILABILITY;
  var daysToAdvertise = hasCustomAvailability ? I18n.moment.utc(customAvailabilityEndDate).diff(I18n.moment.utc(customAvailabilityStartDate), 'days') + 1 : weeksToAdvertise * 7;
  var startDate = hasCustomAvailability ? I18n.moment.utc(customAvailabilityStartDate) : I18n.moment(now).utc().day(0);
  startDate.startOf('day');
  return getListOfDays(startDate, daysToAdvertise);
}
export function availabilityRecordToDateTimeRangesFromStart(availability, bookableDays, timezone) {
  /**
   * Converts the user's configured weekly availability to a List of TimeRanges
   * representing epoch timestamps of availability for a given list of days
   * @param {AvailabilityRecord} availability - A map of weekdays to configured availability
   * for that day, expressed as minutes from the start of the day
   * @param {List} bookableDays - List of days on which meetings can be booked
   * @param {string} timezone - Configured timezone on meeting link
   * @param {int} now - Timestamp representing this moment
   * @return {List} List of TimeRange, representing availability as configured as
   * epoch timestamps for the given list of days
   */
  return bookableDays.map(function (date) {
    return (// transform static configured availablity for a day -> a list of epoch timeranges
      availability.getForDateString(date).map(function (timeRange) {
        return timeRangeToDateTimeRange({
          timeRange: timeRange,
          date: date,
          timezone: timezone
        });
      })
    );
  }).flatten(true).filter(identity);
}
export function getConfiguredAvailableTimeRanges(availability, weeksToAdvertise, customAvailabilityStartDate, customAvailabilityEndDate, timezone, now) {
  /**
   * Converts the user's configured weekly availability to a List of TimeRanges
   * starting from the proper start date, accounting for custom availability.
   * @param {AvailabilityRecord} availability - A map of weekdays to configured availability
   * for that day
   * @param {int} weeksToAdvertise - Number of weeks ahead a meeting can be booked
   * @param {int} customAvailabilityStartDate - Date custom availability starts
   * @param {int} customAvailabilityEndDate - Date custom availability ends
   * @param {string} timezone - Configured timezone on meeting link
   * @param {int} now - Timestamp representing this moment
   * @return {List} List of TimeRange, starting from the correct start date, in the user's
   * configured timezone, representing available times as configured.
   */
  var bookableDays = getBookableDays(weeksToAdvertise, customAvailabilityStartDate, customAvailabilityEndDate, now);
  return availabilityRecordToDateTimeRangesFromStart(availability, bookableDays, timezone);
}
export function isQA() {
  return enviro.getInternal('api') === 'qa';
}
export function getEnvPostfix() {
  return isQA() ? 'qa' : '';
}
export function getAnalyticsScriptSource(portalId, embedded) {
  var qaPostfix = isQA() ? 'qa' : '';
  var embeddedQueryParam = embedded ? '?analyticsCommon=true' : '';
  return "//js.hs-scripts" + qaPostfix + ".com/" + portalId + ".js" + embeddedQueryParam;
}
export function getAnalyticsCanonicalUrl() {
  var origin = window.location.origin || window.location.protocol + "//" + window.location.hostname;
  return "" + origin + window.location.pathname;
}
export function getVisibleUserTag(userProfile) {
  var formattedName = formatName({
    firstName: get('firstName', userProfile),
    lastName: get('lastName', userProfile)
  });
  return formattedName || get('email', userProfile);
}